// 15's settings registration — the entry `app/bootstrap.ts` has carried as "Still to come" since
// module 22's host seam landed (ORP-20405).
//
// A function, not a module side effect, for the same reason as 22's own installer and 11's/12's:
// `resetSettingsRegistry()` clears nav entries wholesale, so an entry registered at module-eval time
// survives exactly until the first spec that resets — then silently vanishes for the rest of that
// file. A registration a test can re-run is the only shape that survives.
//
// The registrar arrives as an ARGUMENT (contract 22 §8). 15 never imports 22's registry and 22 never
// imports 15; `app/bootstrap.ts` is the only place that knows both exist.
//
import type { RouteRecordRaw } from 'vue-router'

import { useInsightsRbac } from './composables/useInsightsRbac'
import {
  INSIGHT_AUTHORING_PREVIEW_ROUTE,
  INSIGHT_CONFIG_PREVIEW_LIST_ROUTE,
  INSIGHT_SETTINGS_NAV_KEYS,
  INSIGHT_WORKBENCH_ROUTE,
  insightSettingsRoutes,
} from './routes'

import type { SettingsNavEntry } from '@/features/workspace-settings'

/** The half of contract 22 §8's registrar this feature uses. Typed as its own interface so the
 *  composition root can keep handing over the whole registrar unchanged. */
export interface InsightsSettingsRegistrar {
  registerNav: (entry: SettingsNavEntry) => void
  registerRoutes: (records: RouteRecordRaw[]) => void
}

/**
 * Register 15's three sysadmin surfaces into module 22's settings nav.
 *
 * All three land after Profile and before Connections, matching New Front's settings root order.
 */
export function installInsightsSettingsNav(reg: InsightsSettingsRegistrar): void {
  reg.registerRoutes(insightSettingsRoutes)

  // Read once: `useInsightsRbac` is cold-boot safe (pre-`ready` the account store defaults give
  // `isSysAdmin=false`), and 22 calls `visible()` on every nav render, so the computed re-evaluates
  // as soon as the bootstrap `rbac` section lands.
  const rbac = useInsightsRbac()

  // 15's OWN i18n namespace on every label — contract 22 §5: 22 renders an entry's label, it never
  // owns the copy. `visible` is the feature's own sysadmin gate, the same bit `requiresSysAdmin`
  // makes the router enforce (§12 defence in depth: hidden in the nav, refused by the guard).
  reg.registerNav({
    key: INSIGHT_SETTINGS_NAV_KEYS.workbench,
    labelKey: 'insights.nav.settings.workbench',
    icon: 'sliders',
    group: 'insights',
    order: 100,
    routeName: INSIGHT_WORKBENCH_ROUTE.name,
    visible: () => rbac.canUseWorkbench.value,
  })

  reg.registerNav({
    key: INSIGHT_SETTINGS_NAV_KEYS.configs,
    labelKey: 'insights.nav.settings.configs',
    icon: 'clipboard-list',
    group: 'insights',
    order: 110,
    routeName: INSIGHT_CONFIG_PREVIEW_LIST_ROUTE.name,
    visible: () => rbac.canAuthorInsights.value,
  })

  reg.registerNav({
    key: INSIGHT_SETTINGS_NAV_KEYS.authoring,
    labelKey: 'insights.nav.settings.authoring',
    icon: 'square-pen',
    group: 'insights',
    order: 120,
    routeName: INSIGHT_AUTHORING_PREVIEW_ROUTE.name,
    visible: () => rbac.canAuthorInsights.value,
  })
}
